import { CommonModule, formatDate } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { CatalogCrudModalComponent } from '../../catalog/shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../../catalog/shared/components/catalog-delete-confirm-modal';
import { CatalogModalController } from '../../catalog/shared/catalog-modal.controller';
import {
  buildDocumentUploadToast,
  createDocumentUploadEntries,
  DocumentUploadEntry,
  summarizeDocumentUploads,
  updateDocumentUploadEntry,
  uploadDocumentsSequentially,
} from '../../shared/document-upload';
import { DocumentUploadList } from '../../shared/document-upload-list';
import { DocumentNameCell } from '../../shared/document-name-cell';
import { DocumentDescriptionCell } from '../../shared/document-description-cell';
import { resolveApiErrorMessage } from '../../shared/api-error';
import {
  BondDefinitionDetail,
  BondDefinitionUpsertRequest,
  BondDocumentItem,
  BondFormulaItem,
  BondNoteItem,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  NoteCreateRequest,
} from './bond-definition.types';
import { BondDefinitionService } from './bond-definition.service';

type DefinitionTabId = 'definition' | 'documentation' | 'notes';

interface DefinitionFormValue {
  bondDocumentationId: number | null;
  bondProceedId: number | null;
  bondProceedSubtypeId: number | null;
  bondPricingSupplement: string;
  bondFormatId: number | null;
  bondListingId: number | null;
}

interface AddNoteFormValue {
  note: string;
}

interface AddDocumentFormValue {
  description: string;
  filename: string;
  files: File[];
}

@Component({
  selector: 'app-bond-definition',
  imports: [CommonModule, CatalogCrudModalComponent, CatalogDeleteConfirmModalComponent, DocumentUploadList, DocumentNameCell, DocumentDescriptionCell],
  templateUrl: './bond-definition.html',
  styleUrl: './bond-definition.css',
})
export class BondDefinition implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('documentFilesInput')
  private documentFilesInput?: ElementRef<HTMLInputElement>;

  private readonly notesSource = 'BondDefinition';
  private readonly documentSource = 'BondDefinition';
  private readonly allowedDocumentTypes = ['.doc', '.msg', '.pdf', '.xls'];
  private readonly modalController = new CatalogModalController();
  private readonly formulasModalId = 'bond_definition_formulas_modal';
  private readonly addDocumentModalId = 'bond_definition_add_document_modal';
  private readonly deleteDocumentModalId = 'bond_definition_delete_document_modal';
  private readonly addNoteModalId = 'bond_definition_add_note_modal';
  private readonly deleteNoteModalId = 'bond_definition_delete_note_modal';

  private detailSubscription?: Subscription;
  private saveSubscription?: Subscription;
  private noteMutationSubscription?: Subscription;
  private documentsSubscription?: Subscription;
  private documentMutationSubscription?: Subscription;
  private addDocumentModalCloseObserver?: MutationObserver;

  protected readonly securityId: string;
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly detail = signal<BondDefinitionDetail | null>(null);
  protected readonly hasUnsavedChanges = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly saveError = signal('');
  protected readonly initializeError = signal('');
  protected readonly isInitializing = signal(false);
  protected readonly activeTab = signal<DefinitionTabId>('definition');
  protected readonly definitionForm = signal<DefinitionFormValue>({
    bondDocumentationId: null,
    bondProceedId: null,
    bondProceedSubtypeId: null,
    bondPricingSupplement: '',
    bondFormatId: null,
    bondListingId: null,
  });

  protected readonly addFormError = signal('');
  protected readonly isMutatingDocument = signal(false);
  protected readonly renamingDocumentId = signal<number | null>(null);
  protected readonly describingDocumentId = signal<number | null>(null);
  protected readonly documentUploadEntries = signal<DocumentUploadEntry[]>([]);
  protected readonly isMutatingNote = signal(false);
  protected readonly addDocumentForm = signal<AddDocumentFormValue>({
    description: '',
    filename: '',
    files: [],
  });
  protected readonly addNoteForm = signal<AddNoteFormValue>({ note: '' });
  protected readonly editingDocumentId = signal<number | null>(null);
  protected readonly editingNoteId = signal<number | null>(null);
  protected readonly pendingDocumentDeleteId = signal<number | null>(null);
  protected readonly pendingDocumentDeletePreview = signal('');
  protected readonly pendingNoteDeleteId = signal<number | null>(null);
  protected readonly pendingNoteDeletePreview = signal('');

  protected readonly filteredProceedSubtypes = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return [];
    }

    const selectedProceedId = this.definitionForm().bondProceedId;
    if (selectedProceedId === null) {
      return detail.bondProceedsSubtype;
    }

    return detail.bondProceedsSubtype.filter((entry) => entry.proceedId === selectedProceedId);
  });

  protected readonly hasFormulas = computed(() => (this.detail()?.formulas.length ?? 0) > 0);

  protected readonly defaultDocumentationId = computed<number | null>(() => {
    const detail = this.detail();
    if (!detail) {
      return null;
    }

    const defaultName = this.resolveDocumentationDefaultName(detail.securityType, detail.ccy);
    if (!defaultName) {
      return null;
    }

    const match = detail.bondDocumentations.find((entry) => entry.name.trim().toUpperCase() === defaultName);
    return match?.id ?? null;
  });

  protected readonly shouldShowDocumentationDefaultWarning = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return false;
    }

    return detail.bondDocumentationId === null && this.defaultDocumentationId() !== null;
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly titleService: Title,
    private readonly bondDefinitionService: BondDefinitionService
  ) {
    this.securityId = this.route.snapshot.paramMap.get('securityId') ?? '';
  }

  ngOnInit(): void {
    this.updateDocumentTitle();
    this.loadDetail();
  }

  ngAfterViewInit(): void {
    this.addDocumentModalCloseObserver = this.modalController.observeClose(this.addDocumentModalId, () => {
      this.resetAddDocumentForm();
    });
  }

  ngOnDestroy(): void {
    this.titleService.setTitle('FRIDA');
    this.detailSubscription?.unsubscribe();
    this.saveSubscription?.unsubscribe();
    this.noteMutationSubscription?.unsubscribe();
    this.documentsSubscription?.unsubscribe();
    this.documentMutationSubscription?.unsubscribe();
    this.addDocumentModalCloseObserver?.disconnect();
  }

  protected onRetry(): void {
    this.loadDetail();
  }

  protected setActiveTab(tab: DefinitionTabId): void {
    this.activeTab.set(tab);
  }

  protected isTabActive(tab: DefinitionTabId): boolean {
    return this.activeTab() === tab;
  }

  protected formatSettlementDate(value: string): string {
    if (!value) {
      return '-';
    }

    const compactMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(value.trim());
    if (compactMatch) {
      const year = Number(compactMatch[1]);
      const monthIndex = Number(compactMatch[2]) - 1;
      const day = Number(compactMatch[3]);
      const compactDate = new Date(year, monthIndex, day);

      // Prevent invalid rollovers such as 20210230 becoming March 2.
      if (
        compactDate.getFullYear() === year
        && compactDate.getMonth() === monthIndex
        && compactDate.getDate() === day
      ) {
        return formatDate(compactDate, 'MM/dd/yyyy', 'en-US');
      }
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return formatDate(parsed, 'MM/dd/yyyy', 'en-US');
  }

  protected formatDateTime(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  protected formatDateTimeOrBlank(value: string): string {
    if (!value) {
      return '';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  protected toSelectValue(value: number | null): string {
    return value === null ? '' : String(value);
  }

  protected isSelectOptionSelected(currentValue: number | null, optionId: number): boolean {
    if (currentValue === null) {
      return false;
    }

    return String(currentValue) === String(optionId);
  }

  protected couponMaturityValue(): string {
    const detail = this.detail();
    if (!detail) {
      return '-';
    }

    const coupon = detail.coupon === null
      ? '-'
      : new Intl.NumberFormat('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(detail.coupon);

    return `${coupon} \u00A0\u00A0 ${this.formatSettlementDate(detail.maturityDate)}`.trim();
  }

  protected openFormulasModal(): void {
    if (!this.hasFormulas()) {
      return;
    }

    this.modalController.open(this.formulasModalId);
  }

  protected documentModalTitle(): string {
    return this.editingDocumentId() === null ? 'Upload Documentation' : 'Edit Documentation';
  }

  protected documentSubmitLabel(): string {
    return this.editingDocumentId() === null ? 'Upload Documents' : 'Save Changes';
  }

  protected resetAddDocumentForm(): void {
    this.addFormError.set('');
    this.editingDocumentId.set(null);
    this.documentUploadEntries.set([]);
    if (this.documentFilesInput) {
      this.documentFilesInput.nativeElement.value = '';
    }
    this.addDocumentForm.set({
      description: '',
      filename: '',
      files: [],
    });
  }

  private closeAddDocumentModalAfterMutation(): void {
    requestAnimationFrame(() => this.modalController.close(this.addDocumentModalId));
  }

  protected onDefinitionSelectChange(field: keyof Omit<DefinitionFormValue, 'bondPricingSupplement'>, value: string): void {
    const parsed = this.parseNullableNumber(value);

    this.definitionForm.update((current) => {
      const next: DefinitionFormValue = {
        ...current,
        [field]: parsed,
      };

      if (field === 'bondProceedId') {
        const subtypeIsValid = this.filteredProceedSubtypes().some((entry) => entry.id === next.bondProceedSubtypeId);
        if (!subtypeIsValid) {
          next.bondProceedSubtypeId = null;
        }
      }

      return next;
    });

    this.hasUnsavedChanges.set(true);
    this.saveError.set('');
  }

  protected onDefinitionTextChange(field: 'bondPricingSupplement', value: string): void {
    this.definitionForm.update((current) => ({
      ...current,
      [field]: value,
    }));

    this.hasUnsavedChanges.set(true);
    this.saveError.set('');
  }

  protected saveDefinition(): void {
    const detail = this.detail();
    if (!detail || !this.securityId.trim()) {
      return;
    }

    this.isSaving.set(true);
    this.saveError.set('');
    this.saveSubscription?.unsubscribe();

    this.saveSubscription = this.bondDefinitionService.saveBondDefinition(this.buildUpsertPayload(detail)).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.hasUnsavedChanges.set(false);
        window.KTToast?.show({
          message: 'Bond definition saved successfully.',
          variant: 'success',
        });
        this.loadDetail();
      },
      error: () => {
        this.isSaving.set(false);
        this.saveError.set('Unable to save bond definition. Please try again.');
      },
    });
  }

  protected onProceedWithNewBond(): void {
    const detail = this.detail();
    if (!detail || this.isInitializing()) {
      return;
    }

    this.isInitializing.set(true);
    this.initializeError.set('');
    this.saveSubscription?.unsubscribe();

    this.saveSubscription = this.bondDefinitionService.saveBondDefinition(this.buildUpsertPayload(detail)).subscribe({
      next: () => {
        this.isInitializing.set(false);
        window.KTToast?.show({
          message: 'Bond definition was created in Frida.',
          variant: 'success',
        });
        this.loadDetail();
      },
      error: () => {
        this.isInitializing.set(false);
        this.initializeError.set('Unable to create this bond definition in Frida. Please try again.');
      },
    });
  }

  protected noteModalTitle(): string {
    return this.editingNoteId() === null ? 'Add Note' : 'Edit Note';
  }

  protected noteSubmitLabel(): string {
    return this.editingNoteId() === null ? 'Add Note' : 'Save Changes';
  }

  protected trackDocument(_: number, item: BondDocumentItem): string {
    return `${item.id}-${item.description}-${item.filename}`;
  }

  protected documentFilenames(documents: readonly BondDocumentItem[]): string[] {
    return documents.map((document) => document.filename);
  }

  protected renameDocument(document: BondDocumentItem, filename: string): void {
    this.saveDocumentInline(document, 'filename', filename);
  }

  protected updateDocumentDescription(document: BondDocumentItem, description: string | null): void {
    this.saveDocumentInline(document, 'description', description);
  }

  private saveDocumentInline(document: BondDocumentItem, field: 'filename' | 'description', value: string | null): void {
    const currentDetail = this.detail();
    if (!currentDetail || this.isMutatingDocument()) {
      return;
    }

    const payload: DocumentUpdateRequest = {
      parentElementId: currentDetail.tradeId || null,
      securityId: currentDetail.secId || this.securityId || null,
      description: field === 'description' ? value : document.description || null,
      type: document.type || null,
      filename: field === 'filename' ? (value ?? document.filename) : document.filename,
      ezShareId: document.ezShareId || null,
      ezShareLink: document.ezShareLink || null,
      source: this.documentSource,
    };

    this.isMutatingDocument.set(true);
    const savingDocumentId = field === 'filename' ? this.renamingDocumentId : this.describingDocumentId;
    savingDocumentId.set(document.id);
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = this.bondDefinitionService.updateDocument(document.id, payload).subscribe({
      next: () => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        this.refreshDocumentsBySecurity();
        window.KTToast?.show({
          message: field === 'filename' ? `Document renamed to '${value}'.` : 'Document description updated.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        window.KTToast?.show({
          message: resolveApiErrorMessage(
            field === 'filename'
              ? 'Unable to rename document. Please try again.'
              : 'Unable to update document description. Please try again.',
            error,
          ),
          variant: 'destructive',
        });
      },
    });
  }

  protected startEditDocumentRow(row: BondDocumentItem): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.addFormError.set('');
    this.editingDocumentId.set(row.id);
    this.addDocumentForm.set({
      description: row.description,
      filename: row.filename,
      files: [],
    });
  }

  protected updateAddDocumentField(field: 'description' | 'filename', value: string): void {
    this.addDocumentForm.update((current) => ({
      ...current,
      [field]: value,
    }));
    this.addFormError.set('');
  }

  protected onDocumentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];

    this.addDocumentForm.update((current) => ({
      ...current,
      files,
    }));
    this.documentUploadEntries.set(createDocumentUploadEntries(files));
    this.addFormError.set('');
  }

  protected addOrUpdateDocument(): void {
    const currentDetail = this.detail();
    if (!currentDetail || this.isMutatingDocument()) {
      return;
    }

    const form = this.addDocumentForm();
    const editingDocumentId = this.editingDocumentId();
    if (editingDocumentId !== null) {
      const filename = form.filename.trim();
      if (!filename) {
        this.addFormError.set('Filename is required.');
        return;
      }

      const currentDocument = currentDetail.documentsAttached.find((document) => document.id === editingDocumentId);
      if (!currentDocument) {
        this.addFormError.set('Unable to identify the document to update.');
        return;
      }

      const payload: DocumentUpdateRequest = {
        parentElementId: currentDetail.tradeId || null,
        securityId: currentDetail.secId || this.securityId || null,
        description: form.description.trim() || null,
        type: currentDocument.type || null,
        filename,
        ezShareId: currentDocument.ezShareId || null,
        ezShareLink: currentDocument.ezShareLink || null,
        source: this.documentSource,
      };

      this.isMutatingDocument.set(true);
      this.addFormError.set('');
      this.documentMutationSubscription?.unsubscribe();
      this.documentMutationSubscription = this.bondDefinitionService.updateDocument(editingDocumentId, payload).subscribe({
        next: () => {
          this.isMutatingDocument.set(false);
          this.closeAddDocumentModalAfterMutation();
          this.refreshDocumentsBySecurity();
          window.KTToast?.show({ message: 'Document metadata updated successfully.', variant: 'success' });
        },
        error: () => {
          this.isMutatingDocument.set(false);
          this.addFormError.set('Unable to update document. Please try again.');
        },
      });

      return;
    }

    if (form.files.length === 0) {
      this.addFormError.set('Select at least one file to upload.');
      return;
    }

    const reservedFilenames = new Set(
      currentDetail.documentsAttached
        .map((document) => document.filename.trim().toUpperCase())
        .filter((filename) => filename.length > 0),
    );
    const invalidFiles: string[] = [];
    const payloads: DocumentCreateRequest[] = [];

    for (const file of form.files) {
      const extension = this.extractFileExtension(file.name);
      if (!extension || !this.allowedDocumentTypes.includes(extension)) {
        invalidFiles.push(file.name || '(unknown file)');
        continue;
      }

      let generatedFilename = '';
      let attempts = 0;
      do {
        generatedFilename = `${this.generateDocumentCode()}.${extension.slice(1)}`.toUpperCase();
        attempts += 1;
      } while (reservedFilenames.has(generatedFilename) && attempts < 10);

      if (reservedFilenames.has(generatedFilename)) {
        this.addFormError.set('Unable to generate a unique filename code. Please retry upload.');
        return;
      }
      reservedFilenames.add(generatedFilename);

      payloads.push({
        filename: generatedFilename,
        parentElementId: currentDetail.tradeId || null,
        securityId: currentDetail.secId || this.securityId || null,
        description: '',
        type: extension,
        source: this.documentSource,
        modifiedBy: this.resolveDocumentModifiedBy(),
        file,
      });
    }

    if (invalidFiles.length > 0) {
      this.addFormError.set(
        `Unsupported file type: ${invalidFiles.join(', ')}. Allowed types: ${this.allowedDocumentTypes.join(', ')}.`,
      );
      return;
    }

    this.isMutatingDocument.set(true);
    this.addFormError.set('');
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = uploadDocumentsSequentially(
      payloads,
      (payload) => this.bondDefinitionService.createDocument(payload),
      (file, status, message) =>
        this.documentUploadEntries.update((entries) => updateDocumentUploadEntry(entries, file, status, message)),
    ).subscribe((outcomes) => {
      const summary = summarizeDocumentUploads(outcomes);
      this.isMutatingDocument.set(false);
      this.refreshDocumentsBySecurity();
      window.KTToast?.show(buildDocumentUploadToast(summary));

      if (summary.failed.length === 0) {
        this.closeAddDocumentModalAfterMutation();
        return;
      }

      this.addDocumentForm.update((current) => ({ ...current, files: summary.retryableFiles }));
      if (summary.retryableFiles.length > 0) {
        this.addFormError.set('Some files could not be uploaded. Upload again to retry only the failed files.');
      }
    });
  }

  protected requestDeleteDocumentRow(row: BondDocumentItem): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.pendingDocumentDeleteId.set(row.id);
    this.pendingDocumentDeletePreview.set((row.filename || row.description || String(row.id)).trim().slice(0, 120));
  }

  protected cancelDeleteDocumentRow(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.pendingDocumentDeleteId.set(null);
    this.pendingDocumentDeletePreview.set('');
  }

  protected confirmDeleteDocumentRow(): void {
    const documentId = this.pendingDocumentDeleteId();
    if (documentId === null || this.isMutatingDocument()) {
      return;
    }

    this.isMutatingDocument.set(true);
    this.addFormError.set('');
    this.documentMutationSubscription?.unsubscribe();
    this.documentMutationSubscription = this.bondDefinitionService.deleteDocument(documentId).subscribe({
      next: () => {
        this.refreshDocumentsBySecurity();
        this.modalController.close(this.deleteDocumentModalId);
        this.pendingDocumentDeleteId.set(null);
        this.pendingDocumentDeletePreview.set('');
        this.isMutatingDocument.set(false);
        window.KTToast?.show({ message: 'Document deleted successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingDocument.set(false);
        this.addFormError.set('Unable to delete document. Please try again.');
      },
    });
  }

  protected trackNote(_: number, item: BondNoteItem): string {
    return `${item.id}-${item.note}`;
  }

  protected trackFormula(_: number, item: BondFormulaItem): string {
    return `${item.securityId}-${item.assetId}-${item.applyDate}`;
  }

  protected resetAddNoteForm(): void {
    this.addFormError.set('');
    this.editingNoteId.set(null);
    this.addNoteForm.set({ note: '' });
  }

  protected startEditNoteRow(row: BondNoteItem): void {
    this.addFormError.set('');
    this.editingNoteId.set(row.id);
    this.addNoteForm.set({ note: row.note });
  }

  protected updateAddNoteField(value: string): void {
    this.addNoteForm.set({ note: value });
    this.addFormError.set('');
  }

  protected addOrUpdateNote(): void {
    const noteText = this.addNoteForm().note.trim();
    if (!noteText) {
      this.addFormError.set('Note is required.');
      return;
    }

    const payload = this.buildNotePayload(noteText);
    if (!payload) {
      this.addFormError.set('Security ID is required to save notes.');
      return;
    }

    const editingNoteId = this.editingNoteId();
    this.isMutatingNote.set(true);
    this.noteMutationSubscription?.unsubscribe();

    if (editingNoteId === null) {
      this.noteMutationSubscription = this.bondDefinitionService.createNote(payload).subscribe({
        next: (created) => {
          this.upsertNoteInDetail({
            id: created.id,
            note: created.description,
            author: created.modifiedBy,
            createdAt: created.dateModified,
          });
          this.isMutatingNote.set(false);
          this.resetAddNoteForm();
          this.modalController.close(this.addNoteModalId);
          window.KTToast?.show({ message: 'Note added successfully.', variant: 'success' });
        },
        error: () => {
          this.isMutatingNote.set(false);
          this.addFormError.set('Unable to add note. Please try again.');
        },
      });

      return;
    }

    this.noteMutationSubscription = this.bondDefinitionService.updateNote(editingNoteId, payload).subscribe({
      next: () => {
        this.updateNoteInDetail(editingNoteId, payload.description, payload.modifiedBy);
        this.isMutatingNote.set(false);
        this.resetAddNoteForm();
        this.modalController.close(this.addNoteModalId);
        window.KTToast?.show({ message: 'Note updated successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingNote.set(false);
        this.addFormError.set('Unable to update note. Please try again.');
      },
    });
  }

  protected requestDeleteNoteRow(row: BondNoteItem): void {
    this.pendingNoteDeleteId.set(row.id);
    this.pendingNoteDeletePreview.set(row.note.slice(0, 60));
    this.modalController.open(this.deleteNoteModalId);
  }

  protected cancelDeleteNoteRow(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.pendingNoteDeleteId.set(null);
    this.pendingNoteDeletePreview.set('');
  }

  protected confirmDeleteNoteRow(): void {
    const noteId = this.pendingNoteDeleteId();
    if (noteId === null || this.isMutatingNote()) {
      return;
    }

    this.isMutatingNote.set(true);
    this.noteMutationSubscription?.unsubscribe();

    this.noteMutationSubscription = this.bondDefinitionService.deleteNote(noteId).subscribe({
      next: () => {
        this.removeNoteFromDetail(noteId);
        this.isMutatingNote.set(false);
        this.pendingNoteDeleteId.set(null);
        this.pendingNoteDeletePreview.set('');
        this.modalController.close(this.deleteNoteModalId);
        window.KTToast?.show({ message: 'Note deleted successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingNote.set(false);
        this.addFormError.set('Unable to delete note. Please try again.');
      },
    });
  }

  private loadDetail(): void {
    if (!this.securityId.trim()) {
      this.errorMessage.set('Security ID is missing from route.');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    this.detailSubscription?.unsubscribe();

    this.detailSubscription = this.bondDefinitionService.getBondDefinition(this.securityId).subscribe({
      next: (response) => {
        this.detail.set(response);
        this.applyFormFromDetail(response);
        this.hasUnsavedChanges.set(false);
        this.isLoading.set(false);
      },
      error: () => {
        this.detail.set(null);
        this.isLoading.set(false);
        this.errorMessage.set('Unable to load bond definition from the API.');
      },
    });
  }

  private applyFormFromDetail(detail: BondDefinitionDetail): void {
    const fallbackDocumentationId = detail.bondDocumentationId ?? this.defaultDocumentationIdFromValue(detail);

    this.definitionForm.set({
      bondDocumentationId: fallbackDocumentationId,
      bondProceedId: detail.bondProceedId,
      bondProceedSubtypeId: detail.bondProceedSubtypeId,
      bondPricingSupplement: detail.bondPricingSupplement,
      bondFormatId: detail.bondFormatId,
      bondListingId: detail.bondListingId,
    });
  }

  private defaultDocumentationIdFromValue(detail: BondDefinitionDetail): number | null {
    const defaultName = this.resolveDocumentationDefaultName(detail.securityType, detail.ccy);
    if (!defaultName) {
      return null;
    }

    const match = detail.bondDocumentations.find((entry) => entry.name.trim().toUpperCase() === defaultName);
    return match?.id ?? null;
  }

  private resolveDocumentationDefaultName(securityType: string, ccy: string): string | null {
    const normalizedSecurityType = securityType.trim().toUpperCase();
    const normalizedCcy = ccy.trim().toUpperCase();

    if (normalizedSecurityType === 'CDN') {
      return 'DISC-PROG';
    }

    if (normalizedCcy === 'NZD') {
      return 'NZD-KAURI';
    }

    if (normalizedCcy === 'AUD') {
      return 'AUD-PROG';
    }

    if (normalizedCcy && normalizedCcy !== 'AUD' && normalizedCcy !== 'NZD') {
      return 'GLOB-PROG';
    }

    return null;
  }

  private parseNullableNumber(value: string): number | null {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private buildUpsertPayload(detail: BondDefinitionDetail): BondDefinitionUpsertRequest {
    const form = this.definitionForm();

    return {
      securityId: detail.secId || this.securityId,
      bondProceedId: form.bondProceedId,
      bondProceedSubtypeId: form.bondProceedSubtypeId,
      bondDocumentationId: form.bondDocumentationId,
      bondFormatId: form.bondFormatId,
      bondListingId: form.bondListingId,
      modifiedBy: (detail.modifiedBy || 'FRIDA UI').slice(0, 50),
      dateModified: new Date().toISOString(),
      comments: detail.comments,
      bond_Pricing_Supplement: form.bondPricingSupplement,
      genericField1: detail.genericField1,
      genericField2: detail.genericField2,
      genericField3: detail.genericField3,
    };
  }

  private buildNotePayload(description: string): NoteCreateRequest | null {
    const parentId = this.securityId.trim();
    if (!parentId) {
      return null;
    }

    const modifiedBy = (this.detail()?.modifiedBy ?? '').trim() || 'FRIDA UI';

    return {
      parentId: parentId.slice(0, 20),
      source: this.notesSource,
      description,
      modifiedBy: modifiedBy.slice(0, 50),
    };
  }

  private resolveDocumentModifiedBy(): string {
    return (this.detail()?.modifiedBy ?? '').trim() || 'FRIDA UI';
  }

  private extractFileExtension(filename: string): string | null {
    const normalized = filename.trim();
    const extensionIndex = normalized.lastIndexOf('.');
    if (extensionIndex <= 0 || extensionIndex === normalized.length - 1) {
      return null;
    }

    return normalized.slice(extensionIndex).toLowerCase();
  }

  private generateDocumentCode(length = 12): string {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
  }

  private refreshDocumentsBySecurity(): void {
    const securityId = (this.detail()?.secId || this.securityId).trim();
    if (!securityId) {
      return;
    }

    this.documentsSubscription?.unsubscribe();
    this.documentsSubscription = this.bondDefinitionService.getDocumentsBySecurity(securityId).subscribe({
      next: (documents) => {
        this.detail.update((current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            documentsAttached: documents,
          };
        });
      },
      error: () => {
        // Preserve existing rows when refresh fails.
      },
    });
  }

  private upsertNoteInDetail(note: BondNoteItem): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      const exists = current.notes.some((entry) => entry.id === note.id);
      if (!exists) {
        return {
          ...current,
          notes: [...current.notes, note],
        };
      }

      return {
        ...current,
        notes: current.notes.map((entry) => (entry.id === note.id ? note : entry)),
      };
    });
  }

  private updateNoteInDetail(noteId: number, noteText: string, modifiedBy: string): void {
    const now = new Date().toISOString();

    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        notes: current.notes.map((entry) => (
          entry.id === noteId
            ? {
              ...entry,
              note: noteText,
              author: modifiedBy,
              createdAt: now,
            }
            : entry
        )),
      };
    });
  }

  private removeNoteFromDetail(noteId: number): void {
    this.detail.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        notes: current.notes.filter((entry) => entry.id !== noteId),
      };
    });
  }

  private updateDocumentTitle(): void {
    const suffix = this.securityId ? `: ${this.securityId}` : '';
    this.titleService.setTitle(`Bond Definition${suffix}`);
  }
}
